import React, { memo, useState } from 'react';
import { convertEnumsToReadableStrings } from 'utils/helpers';
import { Upload, List, Form, UploadContent, Image, Typography, Checkbox, FMGModal } from 'components';
import { Row, Col, Button, Select } from 'components';
import { CloseIcon, CloseIconLight, DoneIcon } from 'components/common/icons';
import styles from './styles.module.scss';
import { useAttachmentListQuery } from 'api';

const { Title, Text } = Typography;
const { Dragger } = Upload;

// Define attachment types
interface AttachmentItem {
  type: string;
  extension: string;
  id: string;
  file: any;
  existing: boolean;
}

interface DealAttachment {
  id: string;
  fileName: string;
  extension: string;
  existing: boolean;
  size: number;
  attachmentType: string;
  type: string;
  fileLocation?: string;
  files: Record<string, string>;
}

interface AttachmentsProps {
  fileList: File[];
  error: string;
  attachmentType: string | undefined;
  attachmentTypes: any[];
  attachments: AttachmentItem[];
  disabledActions: boolean;
  setAttachmentType: (type: string | undefined) => void;
  setFileList: (files: File[]) => void;
  handleAddAttachment: () => void;
  handleDeleteAttachment: (index: number) => void;
  handleFileSizeValidation: (file: File) => any;
  handleCancelAttachment: () => void;
  dealId?: string | number; // Add dealId prop for fetching attachments
  service?: string; // Service prop for API
  onSelectDealAttachments?: (selectedAttachments: AttachmentItem[]) => void;
}

export const Attachments: React.FC<AttachmentsProps> = memo(
  ({
    fileList,
    error,
    attachmentType,
    attachmentTypes,
    attachments,
    disabledActions,
    setAttachmentType,
    setFileList,
    handleAddAttachment,
    handleDeleteAttachment,
    handleFileSizeValidation,
    handleCancelAttachment,
    dealId,
    onSelectDealAttachments = () => {},
  }) => {
    // State for modal visibility
    const [isModalVisible, setIsModalVisible] = useState(false);
    // State for selected attachments in the modal
    const [selectedModalAttachments, setSelectedModalAttachments] = useState<AttachmentItem[]>([]);

    // Fetch deal attachments
    const { data = {}, isLoading } = useAttachmentListQuery({
      entityName: 'DEAL',
      entityId: dealId,
      service: 'wrd',
      page: 1,
      size: 100,
    });

    const dealAttachments: DealAttachment[] = data.content || [];

    const isImageFile = (file: File) => {
      return ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'].includes(file?.type);
    };

    const getFileIcon = (file: any) => {
      const types = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
      if (file.name.endsWith('.pdf') || file.extension === 'pdf') {
        return (
          <div className={styles.fileIcon} style={{ backgroundColor: '#e74c3c', color: 'white' }}>
            PDF
          </div>
        );
      } else if (types.some((type) => type.includes(file?.extension))) {
        return (
          <div className={styles.filePreview}>
            <Image src={file?.files?.ATTACHMENTS} width={48} height={48} style={{ objectFit: 'cover' }} />
          </div>
        );
      } else if (file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        return (
          <div className={styles.fileIcon} style={{ backgroundColor: '#27ae60', color: 'white' }}>
            {file.name.split('.').pop()}
          </div>
        );
      } else {
        return (
          <div className={styles.fileIcon} style={{ color: '#3498db', backgroundColor: '#e6f7ff' }}>
            FILE
          </div>
        );
      }
    };

    const getFilePreview = (file: File & { extension: string }) => {
      if (isImageFile(file)) {
        // For images, show a thumbnail
        const imageUrl = URL.createObjectURL(file);
        return (
          <div className={styles.filePreview}>
            <Image src={imageUrl} width={48} height={48} style={{ objectFit: 'cover' }} />
          </div>
        );
      } else {
        // For non-images, show an icon
        return getFileIcon(file);
      }
    };

    // Function to check if an attachment is already selected in the main list
    const isAlreadySelected = (attachment: DealAttachment) => {
      return attachments.some(
        (item) => item.file.name === attachment.fileName && item.type === attachment.attachmentType
      );
    };

    // Toggle selection of an attachment in the modal
    const toggleAttachmentSelection = (attachment: DealAttachment) => {
      const isSelected = selectedModalAttachments.some(
        (item) => item.file.name === attachment.fileName && item.type === attachment.attachmentType
      );

      if (isSelected) {
        setSelectedModalAttachments(
          selectedModalAttachments.filter(
            (item) => !(item.file.name === attachment.fileName && item.type === attachment.attachmentType)
          )
        );
      } else {
        // Convert API attachment to format expected by the component
        const convertedAttachment: any = {
          id: attachment.id,
          type: attachment.attachmentType,
          existing: true,
          file: {
            ...attachment,
            type: attachment.attachmentType,
            name: attachment.fileName,
            extension: attachment.extension,
          },
        };
        setSelectedModalAttachments([...selectedModalAttachments, convertedAttachment]);
      }
    };

    // Handle opening the modal
    const handleOpenModal = () => {
      setSelectedModalAttachments([]);
      setIsModalVisible(true);
    };

    // Handle saving selected attachments from modal
    const handleSaveSelectedAttachments = () => {
      onSelectDealAttachments(selectedModalAttachments);
      setIsModalVisible(false);
    };

    return (
      <>
        <Row justify={'space-between'}>
          <Col>
            <Title level={5} className={styles.sectionTitle}>
              Attachments
            </Title>
          </Col>
          <Col>
            <Text className={styles.available_attach} onClick={handleOpenModal}>
              Select Available Attachment
            </Text>
          </Col>
        </Row>

        {attachments.length > 0 && (
          <List
            dataSource={attachments}
            className={styles.attachmentsList}
            renderItem={(item, index) => (
              <List.Item
                className={styles.listItem}
                actions={[
                  <Button
                    key="delete"
                    size="small"
                    type="link"
                    icon={<CloseIconLight />}
                    onClick={() => handleDeleteAttachment(index)}
                  />,
                ]}
              >
                <Row wrap={false} align="middle" style={{ width: '100%', paddingLeft: 8 }}>
                  <Col flex="48px">{getFilePreview(item.file)}</Col>
                  <Col flex="480px">
                    <Text ellipsis={{ tooltip: true }} style={{ height: 32, marginLeft: 12 }}>
                      {item.type && (
                        <span className={styles.optionTag}>{convertEnumsToReadableStrings(item.type)}</span>
                      )}{' '}
                      {item.file.name}
                    </Text>
                    <div style={{ fontSize: '12px', color: '#8c8c8c', marginLeft: 12 }}>
                      {item.file.size ? Math.ceil(item.file.size / 1000) + ' KB' : 'Size N/A'}
                    </div>
                  </Col>
                </Row>
              </List.Item>
            )}
          />
        )}

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Select
              size="large"
              value={attachmentType}
              options={attachmentTypes}
              onChange={setAttachmentType}
              style={{ width: '100%' }}
              placeholder="Select attachment type"
            />
          </Col>

          <Col span={12}>
            <Form.Item validateStatus={error ? 'error' : ''} help={error || ''}>
              <Dragger
                accept=".csv,.pdf,.png,.jpg,.jpeg"
                style={{ height: 40 }}
                beforeUpload={handleFileSizeValidation}
                fileList={fileList as any}
                onRemove={() => setFileList([])}
                maxCount={1}
              >
                <UploadContent
                  title="Upload or Drag and Drop File"
                  message="Max size: 5MB / Format: CSV, PDF, PNG, JPG, JPEG"
                />
              </Dragger>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Row gutter={4} justify="end">
              <Col>
                <Button
                  disabled={disabledActions}
                  color="default"
                  variant="filled"
                  onClick={handleCancelAttachment}
                  icon={<CloseIcon />}
                />
              </Col>
              <Col>
                <Button disabled={disabledActions} type="primary" icon={<DoneIcon />} onClick={handleAddAttachment} />
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Modal for selecting available attachments */}
        <FMGModal
          title="Attachments"
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          width={500}
          footer={
            <Button block={true} type="primary" onClick={handleSaveSelectedAttachments} className={styles.saveButton}>
              Save
            </Button>
          }
          className={styles.attachmentsModal}
          closeIcon={<CloseIcon />}
          centered
        >
          <List
            dataSource={dealAttachments}
            className={styles.modalAttachmentsList}
            loading={isLoading}
            locale={{ emptyText: 'No attachments available' }}
            renderItem={(item: DealAttachment) => {
              const alreadySelected = isAlreadySelected(item);
              const isSelected = selectedModalAttachments.some(
                (selectedItem) => selectedItem.file.name === item.fileName && selectedItem.type === item.attachmentType
              );

              // Create a file-like object for preview
              const fileObject = {
                name: item.fileName,
                type: item.attachmentType,
                size: item.size,
                extension: item.extension,
                files: item.files,
              } as any;

              return (
                <List.Item
                  className={`${styles.listItem}`}
                  onClick={() => !alreadySelected && toggleAttachmentSelection(item)}
                >
                  <Row wrap={false} align="middle" style={{ width: '100%', paddingLeft: 8 }}>
                    <Col flex="24px">
                      <Checkbox
                        checked={isSelected || alreadySelected}
                        disabled={alreadySelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (!alreadySelected) {
                            toggleAttachmentSelection(item);
                          }
                        }}
                      />
                    </Col>
                    <Col flex="48px" style={{ marginLeft: 12 }}>
                      {getFileIcon(fileObject)}
                    </Col>
                    <Col flex="295px">
                      <Text ellipsis={{ tooltip: true }} style={{ height: 32, marginLeft: 12 }}>
                        {item.attachmentType && (
                          <span className={styles.optionTag}>{convertEnumsToReadableStrings(item.attachmentType)}</span>
                        )}{' '}
                        {item.fileName}
                      </Text>
                      <div style={{ fontSize: '12px', color: '#8c8c8c', marginLeft: 12 }}>
                        {item.size ? Math.ceil(item.size / 1000) + ' KB' : 'Size N/A'}
                      </div>
                    </Col>
                  </Row>
                </List.Item>
              );
            }}
          />
        </FMGModal>
      </>
    );
  }
);
